import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = user
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      // Skip /me if we're about to handle OAuth callback (race-condition guard).
      if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
        setLoading(false);
        return;
      }
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.access_token) localStorage.setItem("factskoot_token", data.access_token);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (data.access_token) localStorage.setItem("factskoot_token", data.access_token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("factskoot_token");
    setUser(false);
  };

  const exchangeGoogleSession = async (sessionId) => {
    const { data } = await api.post("/auth/session", { session_id: sessionId });
    setUser(data.user);
    return data.user;
  };

  /**
   * Activate a session minted by an external flow (e.g. the new
   * self-hosted Google OAuth callback). Persists the JWT under the same
   * key the email/password login uses so /api/auth/me stays valid.
   */
  const setSession = (userObj, accessToken) => {
    if (accessToken) localStorage.setItem("factskoot_token", accessToken);
    setUser(userObj);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout, refresh, exchangeGoogleSession, setSession }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx);
}
